import { colors, headingFont, uiFont } from '../theme';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Feed from '../components/Feed';
import HorizontalShowcase from '../components/HorizontalShowcase';
import TopSelection, { TOP_SELECTION_LIMIT } from '../components/TopSelection';
import GenreFilter from '../components/GenreFilter';
import { getAllProducts } from '../api/products.api';
import { useCart } from '../context/CartContext';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Chip,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  compareProductsByPrice,
  getMediaTypeLabel,
  getProductConditionValue,
  getProductMediaType,
  getTopSelectionProducts,
  getTopSelectionRankById,
  isProductAvailable,
} from '../utils/product';
import Seo from '../components/Seo';

const normalizeStyle = (styleValue) => {
  if (!styleValue) return [];

  return String(styleValue)
    .split(/,|\/|\||;/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const MOBILE_CATALOG_LIMIT = 12;
const STANDARD_CATALOG_LIMIT = 24;
const FIVE_COLUMN_CATALOG_LIMIT = 25;

const MEDIA_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'vinyl', label: 'Vinilos' },
  { value: 'cd', label: 'CDs' },
];

const PAGE_CONFIG = {
  all: {
    path: '/',
    title: 'DiscoXDiscos | Catálogo de discos',
    heading: 'Catálogo de discos',
    description:
      'Explorá vinilos y CDs seleccionados, con datos claros, escucha y consulta directa.',
  },
  vinyl: {
    path: '/vinilos',
    title: 'Vinilos | DiscoXDiscos',
    heading: 'Vinilos',
    description:
      'Explorá vinilos seleccionados, con datos claros, escucha y consulta directa.',
  },
  cd: {
    path: '/cds',
    title: 'CDs | DiscoXDiscos',
    heading: 'CDs',
    description:
      'Explorá CDs seleccionados, con datos claros, escucha y consulta directa.',
  },
};

const getCatalogCountNoun = (mediaType, count) => {
  if (mediaType === 'vinyl') return count === 1 ? 'vinilo' : 'vinilos';
  if (mediaType === 'cd') return count === 1 ? 'CD' : 'CDs';
  return count === 1 ? 'disco' : 'discos';
};

function CatalogSkeletonCard() {
  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 1,
        backgroundColor: colors.surface,
        border: '1px solid var(--dxd-border)',
        boxShadow: '0 12px 28px rgba(0,0,0,0.045)',
      }}
    >
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          width: '100%',
          height: 'auto',
          aspectRatio: '1 / 1',
          backgroundColor: colors.elevated,
        }}
      />

      <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
        <Skeleton animation="wave" width="88%" height={40} />
        <Skeleton animation="wave" width="68%" height={14} sx={{ mt: 0.35 }} />
        <Skeleton animation="wave" width="46%" height={24} sx={{ mt: 0.75 }} />

        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
          <Skeleton animation="wave" variant="rounded" width={40} height={44} />
          <Skeleton animation="wave" variant="rounded" width={40} height={44} />
          <Skeleton animation="wave" variant="rounded" width={40} height={44} />
        </Box>

      </Box>
    </Paper>
  );
}

function HomePage({ initialMediaType = 'all' }) {
  const { syncCatalog } = useCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isFiveColumnDesktop = useMediaQuery('(min-width:1440px)');
  const showcaseUsesGrid = useMediaQuery(theme.breakpoints.up('md'));
  // Below md the carousel fits a third complete 220px card at 734px.
  const fitsThreeShowcaseCovers = useMediaQuery('(min-width:734px)');
  const eagerShowcaseImageCount = isFiveColumnDesktop ? 5 : showcaseUsesGrid ? 4 : fitsThreeShowcaseCovers ? 3 : 2;
  const initialVisibleCount = isMobile
    ? MOBILE_CATALOG_LIMIT
    : isFiveColumnDesktop
    ? FIVE_COLUMN_CATALOG_LIMIT
    : STANDARD_CATALOG_LIMIT;
  const routeMediaType =
    initialMediaType === 'vinyl' || initialMediaType === 'cd' ? initialMediaType : 'all';
  const pageConfig = PAGE_CONFIG[routeMediaType];
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [draftSearchTerm, setDraftSearchTerm] = useState('');
  const [selectedMediaType, setSelectedMediaType] = useState(routeMediaType);
  const [selectedStyle, setSelectedStyle] = useState('Todos');
  const [selectedCondition, setSelectedCondition] = useState('all');
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [sortBy, setSortBy] = useState('latest');
  const [showAllStyles, setShowAllStyles] = useState(false);
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const [selectedTopProduct, setSelectedTopProduct] = useState(null);
  const [selectedTopMediaType, setSelectedTopMediaType] = useState(
    routeMediaType === 'cd' ? 'cd' : 'vinyl'
  );

  const fetchData = useCallback(async (isActive = () => true) => {
    try {
      setLoadingProducts(true);
      setProductsError('');
      const response = await getAllProducts();

      if (isActive()) {
        setAllProducts(response.data || []);
        syncCatalog(response.data);
      }
    } catch (error) {
      console.error('Error cargando productos:', error);
      if (isActive()) {
        setProductsError('No pudimos cargar el catálogo. Probá recargando la página.');
        setAllProducts([]);
      }
    } finally {
      if (isActive()) {
        setLoadingProducts(false);
      }
    }
  }, [syncCatalog]);

  useEffect(() => {
    let active = true;

    fetchData(() => active);

    return () => {
      active = false;
    };
  }, [fetchData]);

  useEffect(() => {
    setVisibleCount(initialVisibleCount);
  }, [initialVisibleCount]);



  const handleFilter = () => {
    setSearchTerm(draftSearchTerm.trim());
    setVisibleCount(initialVisibleCount);
    window.requestAnimationFrame(() => {
      document.getElementById('catalogo')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const styleCounts = useMemo(() => {
    const counts = new Map();

    allProducts.forEach((product) => {
      new Set(normalizeStyle(product.style)).forEach(style => counts.set(style, (counts.get(style) || 0) + 1));
    });

    return counts;
  }, [allProducts]);

  const availableStyles = useMemo(() => ['Todos', ...Array.from(styleCounts.keys()).sort((a, b) => a.localeCompare(b))], [styleCounts]);

  const filteredProducts = useMemo(() => {
    let filtered = [...allProducts];

    if (selectedMediaType !== 'all') {
      filtered = filtered.filter(
        (product) => getProductMediaType(product) === selectedMediaType
      );
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();

      filtered = filtered.filter((product) => {
        const haystack = [
          product.artist,
          product.title,
          product.name,
          product.label,
          product.style,
          product.format,
          product.country,
          product.catalogNumber,
          product.description,
          getMediaTypeLabel(getProductMediaType(product)),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(q);
      });
    }

    if (selectedStyle !== 'Todos') {
      filtered = filtered.filter((product) =>
        normalizeStyle(product.style).some(
          (style) => style.toLowerCase() === selectedStyle.toLowerCase()
        )
      );
    }

    if (selectedCondition !== 'all') {
      filtered = filtered.filter(
        (product) => getProductConditionValue(product) === selectedCondition
      );
    }

    if (onlyAvailable) {
      filtered = filtered.filter((product) => isProductAvailable(product));
    }

    switch (sortBy) {
      case 'priceAsc':
        filtered.sort((a, b) => compareProductsByPrice(a, b, 'asc'));
        break;
      case 'priceDesc':
        filtered.sort((a, b) => compareProductsByPrice(a, b, 'desc'));
        break;
      case 'featured':
        filtered.sort((a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured)));
        break;
      case 'alpha':
        filtered.sort((a, b) => {
          const titleA = `${a.artist || ''} ${a.title || a.name || ''}`.trim();
          const titleB = `${b.artist || ''} ${b.title || b.name || ''}`.trim();
          return titleA.localeCompare(titleB);
        });
        break;
      case 'latest':
      default:
        filtered.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
        break;
    }

    return filtered;
  }, [
    allProducts,
    searchTerm,
    selectedMediaType,
    selectedStyle,
    selectedCondition,
    onlyAvailable,
    sortBy,
  ]);

  const visibleProducts = useMemo(
    () => filteredProducts.slice(0, visibleCount),
    [filteredProducts, visibleCount]
  );

  const catalogSkeletonItems = useMemo(
    () => Array.from({ length: initialVisibleCount }, (_, index) => index),
    [initialVisibleCount]
  );

  const visibleProductCount = Math.min(visibleProducts.length, filteredProducts.length);
  const hasMoreProducts = visibleProductCount < filteredProducts.length;

  const showcaseProducts = useMemo(
    () =>
      routeMediaType === 'all'
        ? allProducts
        : allProducts.filter(
            (product) => getProductMediaType(product) === routeMediaType
          ),
    [allProducts, routeMediaType]
  );

  const recentProducts = useMemo(() => {
    return [...showcaseProducts]
      .sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
  }, [showcaseProducts]);

  const promotionalProducts = useMemo(
    () => recentProducts.filter((product) => isProductAvailable(product)),
    [recentProducts]
  );

  const topVinylSelection = useMemo(
    () =>
      getTopSelectionProducts(
        allProducts,
        'vinyl',
        TOP_SELECTION_LIMIT
      ),
    [allProducts]
  );
  const topCdSelection = useMemo(
    () => getTopSelectionProducts(allProducts, 'cd', TOP_SELECTION_LIMIT),
    [allProducts]
  );
  const topVinylRankById = useMemo(
    () => getTopSelectionRankById(topVinylSelection, TOP_SELECTION_LIMIT),
    [topVinylSelection]
  );
  const topCdRankById = useMemo(
    () => getTopSelectionRankById(topCdSelection, TOP_SELECTION_LIMIT),
    [topCdSelection]
  );
  const activeTopMediaType =
    routeMediaType === 'all' ? selectedTopMediaType : routeMediaType;
  const topSelectionProducts =
    activeTopMediaType === 'cd' ? topCdSelection : topVinylSelection;
  const topSelectionTitle =
    activeTopMediaType === 'cd' ? 'Top CDs' : 'Top Vinilos';
  const getTopRankForProduct = (product) => {
    const productId = String(product._id || product.id);

    return getProductMediaType(product) === 'cd'
      ? topCdRankById.get(productId)
      : topVinylRankById.get(productId);
  };

  const latestProducts = useMemo(
    () => promotionalProducts.slice(0, isFiveColumnDesktop ? 5 : 4),
    [isFiveColumnDesktop, promotionalProducts]
  );

  return (
    <Box
      sx={{
        background:
          'radial-gradient(ellipse at top right, rgba(139,43,217,0.08), transparent 40%), linear-gradient(var(--dxd-page), var(--dxd-page))',
        color: colors.text,
        pb: { xs: 1.5, md: 2 },
      }}
    >
      <Seo
        title={pageConfig.title}
        description={pageConfig.description}
        path={pageConfig.path}
      />
      <Container sx={{ pt: { xs: 1, sm: 1.5 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateAreas: {
              xs: productsError ? '"main"' : '"latest" "top" "main"',
              lg: productsError ? '"main"' : '"latest top" "main top"',
            },
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              lg: productsError
                ? 'minmax(0, 1fr)'
                : 'minmax(0, 1fr) 260px',
            },
            alignItems: 'start',
            columnGap: { lg: 2.5 },
            rowGap: { xs: 2, lg: 0 },
          }}
        >
          {!productsError && (
            <Box
              sx={{
                gridArea: 'top',
                minWidth: 0,
                alignSelf: 'start',
                position: { lg: 'sticky' },
                top: { lg: 80 },
                maxHeight: { lg: 'calc(100vh - 96px)' },
                '@supports (height: 100dvh)': {
                  maxHeight: { lg: 'calc(100dvh - 96px)' },
                },
                overflowY: { lg: 'auto' },
                overscrollBehaviorY: { lg: 'contain' },
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255,255,255,0.18) transparent',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  borderRadius: 4,
                },
              }}
            >
              <TopSelection
                title={topSelectionTitle}
                products={topSelectionProducts}
                loading={loadingProducts}
                onProductSelect={setSelectedTopProduct}
                selectedMediaType={activeTopMediaType}
                onMediaTypeChange={
                  routeMediaType === 'all'
                    ? setSelectedTopMediaType
                    : undefined
                }
              />
            </Box>
          )}

          {!productsError && (
            <Box id="ultimos-ingresos" sx={{ gridArea: 'latest', minWidth: 0, scrollMarginTop: 88 }}>
              <HorizontalShowcase
                title="Ultimos ingresos"
                products={latestProducts}
                loading={loadingProducts}
                itemCount={isFiveColumnDesktop ? 5 : 4}
                priorityFirstImage
                eagerImageCount={eagerShowcaseImageCount}
                compact
                getTopRank={getTopRankForProduct}
              />
            </Box>
          )}

          <Box sx={{ gridArea: 'main', minWidth: 0 }}>
            <Box
              id="catalogo"
              sx={{ mt: 0, mb: 0, scrollMarginTop: 16 }}
            >
          <Typography
            variant="overline"
            sx={{
              color: colors.accent,
              fontWeight: 900,
              letterSpacing: 1.4,
            }}
          >
            CATALOGO
          </Typography>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontFamily: headingFont,
              fontWeight: 400,
              textTransform: 'uppercase',
              color: colors.text,
              lineHeight: 1.05,
              mb: { xs: 1.5, md: 2 },
              fontSize: { xs: '1.55rem', sm: '1.85rem', md: '2.1rem' },
            }}
          >
            {selectedMediaType === 'vinyl'
              ? 'Vinilos'
              : selectedMediaType === 'cd'
              ? 'CDs'
              : 'Todos los discos'}
          </Typography>

          <Paper
            component="section"
            aria-label="Filtros del catálogo"
            elevation={0}
            sx={{
              p: { xs: 1.5, md: 2 },
              mb: 2,
              borderRadius: 1,
              background: colors.surface,
              border: '1px solid rgba(255,255,255,0.06)',
              }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography
              variant="subtitle2"
              component="h2"
              sx={{
                fontFamily: uiFont,
                fontSize: '0.72rem',
                fontWeight: 500,
                color: colors.secondary,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
              }}
            >
              Filtrar catalogo
            </Typography>
            <Button
              variant="text"
              onClick={() => {
                setDraftSearchTerm('');
                setSearchTerm('');
                setSelectedMediaType(routeMediaType);
                setSelectedStyle('Todos');
                setSelectedCondition('all');
                setOnlyAvailable(false);
                setSortBy('latest');
                setShowAllStyles(false);
                setVisibleCount(initialVisibleCount);
              }}
              sx={{ minHeight: 36, px: 1, color: colors.secondary, fontSize: '0.64rem', whiteSpace: 'nowrap',
                '&:hover': { color: colors.accent, backgroundColor: colors.tint } }}
            >Limpiar filtros</Button>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gap: { xs: 1, md: 1.5 },
                width: '100%',
                mb: 1.5,
              }}
            >
              <TextField
                placeholder="Buscar artista, disco o sello..."
                value={draftSearchTerm}
                size="small"
                inputProps={{ maxLength: 80, 'aria-label': 'Buscar artista, disco o sello' }}
                onChange={(e) => setDraftSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleFilter();
                }}
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: colors.surface,
                    borderRadius: 1,
                    '& fieldset': { borderColor: colors.controlBorder },
                    '&:hover fieldset': { borderColor: colors.accent },
                    '&.Mui-focused fieldset': { borderColor: colors.accent },
                  },
                }}
              />

              <Button
                onClick={handleFilter}
                variant="contained"
                startIcon={<SearchIcon />}
                aria-label="Buscar en el catálogo"
                sx={{
                  backgroundColor: colors.button,
                  minWidth: { xs: 42, md: 112 },
                  px: { xs: 1.2, md: 3 },
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  fontWeight: 800,
                  '& .MuiButton-startIcon': {
                    m: { xs: 0, md: '0 8px 0 -4px' },
                  },
                  '&:hover': { backgroundColor: colors.hover },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                  Buscar
                </Box>
              </Button>
            </Box>

            {routeMediaType === 'all' && (
              <Box role="group" aria-label="Filtrar por soporte" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  flexShrink: 0,
                  fontSize: '0.65rem',
                  color: colors.secondary,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                }}
              >
                Soporte
              </Typography>

              <Box sx={{ display: 'flex', gap: { xs: 0.65, sm: 1 }, flexWrap: 'wrap' }}>
                {MEDIA_FILTER_OPTIONS.map((option) => (
                  <Chip
                    component="button"
                    type="button"
                    key={option.value}
                    label={option.label}
                    clickable
                    aria-pressed={selectedMediaType === option.value}
                    onClick={() => {
                      setSelectedMediaType(option.value);
                      setVisibleCount(initialVisibleCount);
                    }}
                    sx={{
                      borderRadius: 1,
                      fontWeight: 500,
                      fontSize: '0.66rem',
                      height: { xs: 40, md: 36 },
                      backgroundColor:
                        selectedMediaType === option.value ? colors.tint : colors.surface,
                      color:
                        selectedMediaType === option.value ? colors.accent : colors.secondary,
                      border:
                        selectedMediaType === option.value
                          ? `1px solid ${colors.accent}`
                          : `1px solid ${colors.controlBorder}`,
                      '&:hover': {
                        backgroundColor:
                          colors.tint,
                        borderColor: colors.accent,
                      },
                    }}
                  />
                ))}
              </Box>
              </Box>
            )}

            <Box sx={{ mb: 1.25 }}>
              <GenreFilter genres={availableStyles} counts={styleCounts} value={selectedStyle}
                expanded={showAllStyles} onExpandedChange={setShowAllStyles}
                onChange={style => { setSelectedStyle(style); setVisibleCount(initialVisibleCount); }} />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'auto minmax(0, 1fr) minmax(0, 1.2fr)' },
                gap: 1,
                alignItems: 'center',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyAvailable}
                    onChange={(e) => {
                      setOnlyAvailable(e.target.checked);
                      setVisibleCount(initialVisibleCount);
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: colors.accent,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: colors.cta,
                      },
                    }}
                  />
                }
                label="Solo disponibles"
                sx={{
                  m: 0,
                  gridColumn: { xs: '1 / -1', md: 'auto' },
                  color: colors.text,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.8rem',
                  },
                }}
              />

              <Select
                size="small"
                value={selectedCondition}
                inputProps={{ 'aria-label': 'Filtrar por condición' }}
                onChange={(e) => {
                  setSelectedCondition(e.target.value);
                  setVisibleCount(initialVisibleCount);
                }}
                sx={{
                  minWidth: 0,
                  fontSize: '0.8rem',
                  backgroundColor: colors.surface,
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.controlBorder,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.accent,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.accent,
                  },
                }}
              >
                <MenuItem value="all">Condicion: todas</MenuItem>
                <MenuItem value="new">Nuevo</MenuItem>
                <MenuItem value="used">Usado</MenuItem>
              </Select>

              <Select
                size="small"
                value={sortBy}
                inputProps={{ 'aria-label': 'Ordenar catálogo' }}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setVisibleCount(initialVisibleCount);
                }}
                sx={{
                  minWidth: 0,
                  fontSize: '0.8rem',
                  backgroundColor: colors.surface,
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.controlBorder,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.accent,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.accent,
                  },
                }}
              >
                <MenuItem value="latest">Mas recientes</MenuItem>
                <MenuItem value="featured">Destacados primero</MenuItem>
                <MenuItem value="priceAsc">Precio: menor a mayor</MenuItem>
                <MenuItem value="priceDesc">Precio: mayor a menor</MenuItem>
                <MenuItem value="alpha">Orden alfabetico</MenuItem>
              </Select>

            </Box>

            <Typography
              role="status"
              aria-live="polite"
              variant="body2"
              sx={{
                mt: 1,
                fontSize: '0.78rem',
                color: colors.secondary,
              }}
            >
              {loadingProducts
                ? 'Cargando catalogo...'
                : `Mostrando ${visibleProductCount} de ${
                    filteredProducts.length
                  } ${getCatalogCountNoun(
                    selectedMediaType,
                    filteredProducts.length
                  )}.`}
            </Typography>
          </Paper>
        </Box>

        <Box
          component="section"
          aria-label="Discos del catálogo"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            '@media (min-width: 760px) and (max-width: 899.95px)': {
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            },
            '@media (min-width: 1440px)': {
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              gap: 1.5,
            },
            gap: {
              xs: 1.2,
              sm: 2,
              md: 2,
            },
            alignItems: 'stretch',
          }}
        >
          {loadingProducts
            ? catalogSkeletonItems.map((item) => (
                <Box
                  key={`catalog-skeleton-${item}`}
                  sx={{
                    minWidth: 0,
                    display: 'flex',
                  }}
                >
                  <CatalogSkeletonCard />
                </Box>
              ))
            : visibleProducts.map((product) => (
                <Box
                  key={product._id}
                  sx={{
                    minWidth: 0,
                    display: 'flex',
                  }}
                >
                  <Feed
                    product={product}
                    compact
                    topRank={getTopRankForProduct(product)}
                  />
                </Box>
              ))}
        </Box>

        {!loadingProducts && !productsError && hasMoreProducts && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, md: 3 } }}>
            <Button
              variant="contained"
              onClick={() =>
                setVisibleCount((current) =>
                  Math.min(current + initialVisibleCount, filteredProducts.length)
                )
              }
              sx={{
                backgroundColor: colors.button,
                borderRadius: 1,
                px: 3,
                textTransform: 'uppercase',
                fontWeight: 800,
                '&:hover': { backgroundColor: colors.hover },
              }}
            >
              Ver mas discos
            </Button>
          </Box>
        )}

        {productsError && !loadingProducts && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 1,
              backgroundColor: colors.surface,
              border: '1px solid var(--dxd-border)',
            }}
          >
            <Typography sx={{ color: colors.text, fontWeight: 800, mb: 0.5 }}>
              No se pudo cargar el catálogo.
            </Typography>
            <Typography sx={{ color: colors.secondary }}>{productsError}</Typography>
          </Paper>
        )}

        {!loadingProducts && !productsError && !filteredProducts.length && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 1,
              backgroundColor: colors.surface,
              border: '1px solid var(--dxd-border)',
            }}
          >
            <Typography sx={{ color: colors.text, fontWeight: 800, mb: 0.5 }}>
              No encontramos discos con esos filtros.
            </Typography>
            <Typography sx={{ color: colors.secondary }}>
              Probá cambiando el estilo, desactivando “solo disponibles” o buscando
              con otro término.
            </Typography>
          </Paper>
        )}
          </Box>
        </Box>

        {selectedTopProduct && (
          <Feed
            product={selectedTopProduct}
            hideCard
            detailOpen
            onDetailClose={() => setSelectedTopProduct(null)}
          />
        )}

      </Container>
    </Box>
  );
}

export default HomePage;
